import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Chip, Stack, Alert, Snackbar, CircularProgress, Switch, Link, Tooltip,
} from '@mui/material';
import { getPlatformUsers, setPlatformAdmin } from '../../api/saasApi';
import { useMe } from '../../context/MeContext';

const statusColor = (s) => ({ Active: 'success', Pending: 'warning', Rejected: 'default' }[s] || 'default');

/** Cross-tenant user search, and the UI for granting platform admin. */
const PlatformUsers = () => {
  const { me } = useMe();
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async (term) => {
    setLoading(true);
    try {
      const res = await getPlatformUsers(term);
      if (res.data?.succeeded === false) setError(res.data.messages?.[0] || 'Failed to load users.');
      else setUsers(res.data?.data || []);
    } catch (e) {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(search), 350);
    return () => clearTimeout(timer);
  }, [search, load]);

  const toggleAdmin = async (user, next) => {
    setError('');
    try {
      const res = await setPlatformAdmin(user.id, next);
      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not update.');
        return;
      }
      setToast(res.data?.messages?.[0] || 'Updated.');
      load(search);
    } catch (e) {
      setError(e?.response?.data?.messages?.[0] || 'Could not update.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Users</Typography>
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TextField
        fullWidth size="small" sx={{ mb: 2 }}
        placeholder="Search by name, email or clinic…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Clinic</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Membership</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell align="center">Platform admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{u.name}</span>
                      {u.isOwner && <Chip size="small" label="owner" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    {u.tenantId ? (
                      <Link component={RouterLink} to={`/platform/tenants/${u.tenantId}`} underline="hover">
                        {u.tenantName}
                      </Link>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{u.roleName || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={u.membershipStatus} color={statusColor(u.membershipStatus)} />
                  </TableCell>
                  <TableCell>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={u.id === me?.userId ? 'You cannot change your own platform access here' : 'Takes effect after they sign out and back in'}>
                      <span>
                        <Switch
                          size="small"
                          checked={!!u.isPlatformAdmin}
                          disabled={u.id === me?.userId}
                          onChange={(e) => toggleAdmin(u, e.target.checked)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow><TableCell colSpan={7}>No users match that search.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Container>
  );
};

export default PlatformUsers;
